import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CategoryManager from '../components/CategoryManager';
import PHAddressSelect from '../components/PHAddressSelect';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseJobs = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [bidForm, setBidForm] = useState({
    bid_amount: '',
    message: '',
    estimated_duration: '',
    proposed_time: ''
  });
  const [submittingBid, setSubmittingBid] = useState(false);
  const [bidError, setBidError] = useState('');
  const [bidSuccess, setBidSuccess] = useState('');
  const [searchingByCode, setSearchingByCode] = useState(false);
  const [codeSearchResult, setCodeSearchResult] = useState(null);
  const [codeSearchError, setCodeSearchError] = useState('');

  // Check if user is admin
  const effectiveRole = user?.active_role || user?.role;
  const isAdmin = effectiveRole === 'admin' || user?.is_owner || user?.available_roles?.includes('admin');

  // Location-based default scope (barangay -> city -> province -> all) + manual override
  const userBarangay = user?.barangay || '';
  const userCity = user?.city || '';
  const userProvince = user?.province || '';
  const [locScope, setLocScope] = useState(userBarangay ? 'barangay' : 'all');
  const [manualLoc, setManualLoc] = useState({ province: '', city: '', barangay: '' });
  const [showLocFilter, setShowLocFilter] = useState(false);

  const manualActive = !!(manualLoc.barangay || manualLoc.city || manualLoc.province);
  const autoLocActive = !manualActive && locScope !== 'all';
  const inMyBarangay = (loc) =>
    !!userBarangay && (loc || '').toLowerCase().includes(userBarangay.toLowerCase());

  const scopeLabel = () => {
    if (manualActive) return manualLoc.barangay || manualLoc.city || manualLoc.province;
    if (locScope === 'barangay') return `Barangay ${userBarangay}`;
    if (locScope === 'city') return userCity;
    if (locScope === 'province') return userProvince;
    return 'all areas';
  };

  const widenScope = () => {
    const order = ['barangay', 'city', 'province', 'all'];
    const i = order.indexOf(locScope);
    for (let j = i + 1; j < order.length; j++) {
      const s = order[j];
      if (s === 'all') { setLocScope('all'); return; }
      if (s === 'city' && userCity) { setLocScope('city'); return; }
      if (s === 'province' && userProvince) { setLocScope('province'); return; }
    }
    setLocScope('all');
  };

  const clearLocationFilter = () => {
    setManualLoc({ province: '', city: '', barangay: '' });
    setShowLocFilter(false);
    setLocScope(userBarangay ? 'barangay' : 'all');
  };

  // Check if search term looks like a job order code (18 alphanumeric characters)
  const isJobOrderCode = (term) => {
    const cleanTerm = term.replace(/^#/, '').trim().toUpperCase();
    return /^[A-Z]{8}\d{10}$/.test(cleanTerm);
  };

  useEffect(() => {
    fetchCategories();
    fetchJobs();
    if (user) {
      fetchMyBids();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, locScope, manualLoc]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = { status: 'open' };
      if (selectedCategory) params.category = selectedCategory;

      // Manual location override takes priority; otherwise default to user's barangay (strict)
      if (manualLoc.barangay) params.barangay = manualLoc.barangay;
      else if (manualLoc.city) params.city = manualLoc.city;
      else if (manualLoc.province) params.province = manualLoc.province;
      else if (locScope === 'barangay' && userBarangay) params.barangay = userBarangay;
      else if (locScope === 'city' && userCity) params.city = userCity;
      else if (locScope === 'province' && userProvince) params.province = userProvince;

      const response = await axios.get(`${API}/jobs`, { params });
      // Filter out user's own jobs, then float jobs in the user's barangay to the top
      const filteredJobs = (user
        ? response.data.filter(job => job.client_id !== user.id)
        : response.data
      ).sort((a, b) => (inMyBarangay(b.location) ? 1 : 0) - (inMyBarangay(a.location) ? 1 : 0));
      setJobs(filteredJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBids = async () => {
    try {
      const response = await axios.get(`${API}/bids/my`);
      setMyBids(response.data);
    } catch (error) {
      console.error('Error fetching my bids:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Check if searching by job order code
    if (isJobOrderCode(search)) {
      setSearchingByCode(true);
      setCodeSearchError('');
      setCodeSearchResult(null);
      
      try {
        const cleanCode = search.replace(/^#/, '').trim().toUpperCase();
        const response = await axios.get(`${API}/jobs/code/${cleanCode}`);
        setCodeSearchResult(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setCodeSearchError(t('browseJobs.noJobFoundCode', { code: search.replace(/^#/, '').toUpperCase() }));
        } else {
          setCodeSearchError(t('browseJobs.failedSearchCode'));
        }
      } finally {
        setSearchingByCode(false);
      }
    } else {
      // Clear code search result if not searching by code
      setCodeSearchResult(null);
      setCodeSearchError('');
      updateFilters();
    }
  };

  const clearCodeSearch = () => {
    setCodeSearchResult(null);
    setCodeSearchError('');
    setSearch('');
  };

  const updateFilters = () => {
    const params = {};
    if (search) params.q = search;
    if (selectedCategory) params.category = selectedCategory;
    setSearchParams(params);
  };

  const hasAlreadyBid = (jobId) => {
    return myBids.some(bid => bid.job_id === jobId && bid.status !== 'withdrawn');
  };

  const openBidModal = (job) => {
    if (!user) {
      alert(t('browseJobs.alertLoginBid'));
      return;
    }
    const effectiveRole = user.active_role || user.role;
    if (effectiveRole !== 'provider') {
      alert(t('browseJobs.alertSwitchProvider'));
      return;
    }
    // Block unverified users from bidding
    if (!user.id_verified) {
      const status = user.id_verification_status;
      if (status === 'rejected') {
        alert(t('browseJobs.alertKycRejected'));
      } else if (status === 'pending' || status === 'pending_admin_review') {
        alert(t('browseJobs.alertKycPending'));
      } else {
        alert(t('browseJobs.alertKycRequired'));
      }
      return;
    }
    setSelectedJob(job);
    setBidForm({
      bid_amount: job.budget.toString(),
      message: '',
      estimated_duration: '',
      proposed_time: ''
    });
    setBidError('');
    setBidSuccess('');
    setShowBidModal(true);
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setBidError('');
    setBidSuccess('');
    setSubmittingBid(true);

    try {
      await axios.post(`${API}/jobs/${selectedJob.id}/bids`, {
        job_id: selectedJob.id,
        bid_amount: parseFloat(bidForm.bid_amount),
        message: bidForm.message,
        estimated_duration: bidForm.estimated_duration,
        proposed_time: bidForm.proposed_time || null
      });
      
      setBidSuccess(t('browseJobs.bidSuccess'));
      fetchMyBids();
      setTimeout(() => {
        setShowBidModal(false);
        setSelectedJob(null);
      }, 2000);
    } catch (error) {
      setBidError(error.response?.data?.detail || t('browseJobs.failedSubmitBid'));
    } finally {
      setSubmittingBid(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[currency] || currency;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="browse-jobs-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-start gap-3 mb-8">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('browseJobs.title')}
            </h1>
            <p className="text-gray-500 mt-2">{t('browseJobs.subtitle')}</p>
          </div>
          {user && isAdmin && (
            <button
              onClick={() => setShowCategoryManager(true)}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-full hover:bg-gray-800 font-medium text-sm flex items-center gap-2 transition shrink-0 whitespace-nowrap"
              data-testid="edit-categories-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {isAdmin ? t('browseJobs.editCategories') : t('browseJobs.addCategory')}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('browseJobs.searchPlaceholder')}
                className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                data-testid="job-search-input"
              />
              {search && isJobOrderCode(search) && (
                <p className="text-xs text-orange-600 mt-1">
                  {t('browseJobs.searchingByCode')}
                </p>
              )}
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setTimeout(updateFilters, 100);
              }}
              className="h-11 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white transition"
              data-testid="category-filter"
            >
              <option value="">{t('browseJobs.allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={searchingByCode}
              className="h-11 px-6 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition disabled:opacity-50"
              data-testid="search-jobs-btn"
            >
              {searchingByCode ? t('browseJobs.searchingBtn') : t('browseJobs.search')}
            </button>
            <button
              type="button"
              onClick={() => setShowLocFilter((v) => !v)}
              className="h-11 px-4 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition flex items-center gap-1.5"
              data-testid="toggle-location-filter-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('browseJobs.location')}
            </button>
          </form>

          {/* Location override (Province -> City -> Barangay) */}
          {showLocFilter && (
            <div className="mt-4 pt-4 border-t border-gray-100" data-testid="job-location-filter">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">{t('browseJobs.filterByLocation')}</label>
                {manualActive && (
                  <button onClick={clearLocationFilter} className="text-sm text-gray-500 hover:text-gray-700" data-testid="clear-location-filter-btn">
                    {t('browseJobs.clear')}
                  </button>
                )}
              </div>
              <PHAddressSelect
                province={manualLoc.province}
                city={manualLoc.city}
                barangay={manualLoc.barangay}
                onChange={(next) => setManualLoc(next)}
                idPrefix="job-filter"
              />
            </div>
          )}
        </div>

        {/* Location scope banner */}
        {(autoLocActive || manualActive) && !codeSearchResult && (
          <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-6" data-testid="job-location-scope-banner">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('browseJobs.showingJobsIn')} <strong>{scopeLabel()}</strong>
            </p>
            {autoLocActive && locScope !== 'all' ? (
              <button onClick={widenScope} className="text-sm font-semibold text-blue-700 hover:text-blue-900 whitespace-nowrap" data-testid="job-widen-area-btn">
                {t('browseJobs.searchWiderArea')}
              </button>
            ) : manualActive ? (
              <button onClick={clearLocationFilter} className="text-sm font-semibold text-blue-700 hover:text-blue-900 whitespace-nowrap" data-testid="job-reset-location-btn">
                {t('browseJobs.backToMyArea')}
              </button>
            ) : null}
          </div>
        )}

        {/* Code Search Result */}
        {codeSearchResult && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {t('browseJobs.jobFoundByCode')} <span className="text-orange-600">#JO-{codeSearchResult.job_order_code}</span>
              </h3>
              <button
                onClick={clearCodeSearch}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {t('browseJobs.backToAllJobs')}
              </button>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-orange-200 p-6" data-testid="code-search-result">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-mono font-semibold">
                      #JO-{codeSearchResult.job_order_code}
                    </span>
                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                      {codeSearchResult.category}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {t('browseJobs.posted')} {formatDate(codeSearchResult.created_at)}
                    </span>
                    {codeSearchResult.total_bids > 0 && (
                      <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm font-medium">
                        {codeSearchResult.total_bids} {codeSearchResult.total_bids !== 1 ? t('browseJobs.bids') : t('browseJobs.bid')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{codeSearchResult.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{codeSearchResult.description}</p>
                  {codeSearchResult.location && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {codeSearchResult.location}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-4">
                    <img
                      src={codeSearchResult.client_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(codeSearchResult.client_name)}&background=random`}
                      alt={codeSearchResult.client_name}
                      className="w-8 h-8 rounded-full"
                    />
                    <span className="text-gray-600 text-sm">{codeSearchResult.client_name}</span>
                  </div>
                </div>
                <div className="text-right ml-6">
                  <div className="text-2xl font-bold text-gray-900">
                    {getCurrencySymbol(codeSearchResult.currency || 'PHP')}{codeSearchResult.budget?.toLocaleString()}
                  </div>
                  <div className="text-gray-500 text-sm mb-4">{t('browseJobs.budget')}</div>
                  {codeSearchResult.status === 'open' && user && codeSearchResult.client_id !== user.id && (
                    hasAlreadyBid(codeSearchResult.id) ? (
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {t('browseJobs.youveBid')}
                      </span>
                    ) : (
                      <button
                        onClick={() => openBidModal(codeSearchResult)}
                        className="px-6 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition"
                      >
                        {t('browseJobs.submitBid')}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Code Search Error */}
        {codeSearchError && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-center">
            <p className="text-red-600">{codeSearchError}</p>
            <button
              onClick={clearCodeSearch}
              className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
            >
              {t('browseJobs.clearSearch')}
            </button>
          </div>
        )}

        {/* Jobs List */}
        {!codeSearchResult && !codeSearchError && (loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('browseJobs.loadingJobs')}</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-700 text-lg font-medium">
              {(autoLocActive || manualActive) ? t('browseJobs.noOpenJobsIn', { area: scopeLabel() }) : t('browseJobs.noOpenJobs')}
            </p>
            {autoLocActive && locScope !== 'all' ? (
              <button onClick={widenScope} className="mt-4 inline-block bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 font-medium" data-testid="job-empty-widen-area-btn">
                {t('browseJobs.searchWiderArea')}
              </button>
            ) : manualActive ? (
              <button onClick={clearLocationFilter} className="mt-4 inline-block bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 font-medium" data-testid="job-empty-reset-location-btn">
                {t('browseJobs.backToMyArea')}
              </button>
            ) : (
              <p className="text-gray-400 mt-2">{t('browseJobs.checkBackLater')}</p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 hover:border-gray-200 hover:shadow-sm transition-all overflow-hidden"
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      {job.job_order_code && (
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-mono font-semibold" title="Job Order Code">
                          #JO-{job.job_order_code}
                        </span>
                      )}
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                        {job.category}
                      </span>
                      <span className="text-gray-400 text-sm">
                        {t('browseJobs.posted')} {formatDate(job.created_at)}
                      </span>
                      {job.total_bids > 0 && (
                        <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm font-medium">
                          {job.total_bids} {job.total_bids !== 1 ? t('browseJobs.bids') : t('browseJobs.bid')}
                        </span>
                      )}
                      {inMyBarangay(job.location) && (
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-semibold" data-testid={`job-in-barangay-badge-${job.id}`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {t('browseJobs.inYourBarangay')}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 break-words">{job.title}</h3>
                    <p className="text-gray-500 mb-4 line-clamp-2 break-words">{job.description}</p>
                    
                    {/* Job Reference Images */}
                    {job.images && job.images.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('browseJobs.referencePhotos', { count: job.images.length })}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {job.images.slice(0, 4).map((img, idx) => (
                            <img
                              key={`${img}-${idx}`}
                              src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                              alt={`Job reference ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0 hover:opacity-90 cursor-pointer transition"
                              onClick={() => window.open(img.startsWith('http') ? img : `${BACKEND_URL}${img}`, '_blank')}
                              data-testid={`job-image-${job.id}-${idx}`}
                            />
                          ))}
                          {job.images.length > 4 && (
                            <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex-shrink-0 flex items-center justify-center text-gray-500 text-sm font-medium">
                              {t('browseJobs.moreImages', { count: job.images.length - 4 })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {job.location && (
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location}
                        </span>
                      )}
                      {job.deadline && (
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('browseJobs.due')} {formatDate(job.deadline)}
                        </span>
                      )}
                      {job.preferred_date && (
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('browseJobs.preferred')} {formatDate(job.preferred_date)}
                        </span>
                      )}
                      {(job.preferred_start_time || job.preferred_end_time) && (
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {job.preferred_start_time && job.preferred_end_time 
                            ? `${job.preferred_start_time} - ${job.preferred_end_time}`
                            : job.preferred_start_time || job.preferred_end_time}
                        </span>
                      )}
                      {/* Client with Avatar */}
                      <span className="flex items-center gap-2">
                        {job.client_avatar ? (
                          <img
                            src={job.client_avatar.startsWith('http') ? job.client_avatar : `${BACKEND_URL}${job.client_avatar}`}
                            alt={job.client_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center text-white text-xs font-medium">
                            {job.client_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        {job.client_name}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 sm:block sm:text-right sm:ml-6 sm:flex-shrink-0 pt-3 sm:pt-0 border-t border-gray-100 sm:border-0">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {getCurrencySymbol(job.currency || 'PHP')}{job.budget.toLocaleString()}
                      </p>
                      <p className="text-gray-400 text-sm sm:mb-4">{t('browseJobs.budget')}</p>
                    </div>
                    
                    {hasAlreadyBid(job.id) ? (
                      <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('browseJobs.bidSubmittedBadge')}
                      </span>
                    ) : (
                      <button
                        onClick={() => openBidModal(job)}
                        className="bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 font-medium transition whitespace-nowrap"
                        data-testid={`bid-btn-${job.id}`}
                      >
                        {t('browseJobs.submitBid')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Bid Modal */}
      {showBidModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bid-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">{t('browseJobs.submitYourBid')}</h2>
            </div>

            <div className="p-6">
              {/* Job Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                <h3 className="font-bold text-gray-900">{selectedJob.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('browseJobs.client')} {selectedJob.client_name}</p>
                <p className="text-gray-900 font-bold mt-2">
                  {t('browseJobs.budgetColon')} {getCurrencySymbol(selectedJob.currency || 'PHP')}{selectedJob.budget.toLocaleString()}
                </p>
                
                {/* Job Schedule Details */}
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                  {selectedJob.preferred_date && (
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span><strong>{t('browseJobs.preferredDate')}</strong> {formatDate(selectedJob.preferred_date)}</span>
                    </p>
                  )}
                  {(selectedJob.preferred_start_time || selectedJob.preferred_end_time) && (
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>{t('browseJobs.preferredTime')}</strong> {selectedJob.preferred_start_time && selectedJob.preferred_end_time 
                        ? `${selectedJob.preferred_start_time} - ${selectedJob.preferred_end_time}`
                        : selectedJob.preferred_start_time || selectedJob.preferred_end_time}</span>
                    </p>
                  )}
                  {selectedJob.deadline && (
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span><strong>{t('browseJobs.completionDeadline')}</strong> {formatDate(selectedJob.deadline)}</span>
                    </p>
                  )}
                </div>
                
                {/* Reference Images in Modal */}
                {selectedJob.images && selectedJob.images.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">{t('browseJobs.referencePhotosModal')}</p>
                    <div className="flex gap-2 overflow-x-auto">
                      {selectedJob.images.map((img, idx) => (
                        <img
                          key={`${img}-${idx}`}
                          src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                          alt={`Reference ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0 cursor-pointer hover:opacity-80 transition"
                          onClick={() => window.open(img.startsWith('http') ? img : `${BACKEND_URL}${img}`, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {bidError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                  {bidError}
                </div>
              )}
              {bidSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl">
                  {bidSuccess}
                </div>
              )}

              <form onSubmit={handleSubmitBid}>
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    {t('browseJobs.yourBidAmount')} ({getCurrencySymbol(selectedJob.currency || 'PHP')}) *
                  </label>
                  <input
                    type="number"
                    value={bidForm.bid_amount}
                    onChange={(e) => setBidForm({ ...bidForm, bid_amount: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                    required
                    min="1"
                    step="0.01"
                    data-testid="bid-amount-input"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    {t('browseJobs.yourProposal')} *
                  </label>
                  <textarea
                    value={bidForm.message}
                    onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                    placeholder={t('browseJobs.proposalPlaceholder')}
                    required
                    data-testid="bid-message-input"
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    {t('browseJobs.estimatedDuration')}
                  </label>
                  <input
                    type="text"
                    value={bidForm.estimated_duration}
                    onChange={(e) => setBidForm({ ...bidForm, estimated_duration: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                    placeholder={t('browseJobs.durationPlaceholder')}
                    data-testid="bid-duration-input"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    {t('browseJobs.proposedStartTime')}
                  </label>
                  <input
                    type="time"
                    value={bidForm.proposed_time}
                    onChange={(e) => setBidForm({ ...bidForm, proposed_time: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                    data-testid="bid-time-input"
                  />
                  <p className="text-sm text-gray-400 mt-1">{t('browseJobs.startTimeHint')}</p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBidModal(false);
                      setSelectedJob(null);
                    }}
                    className="flex-1 h-11 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition"
                    data-testid="cancel-bid-btn"
                  >
                    {t('browseJobs.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submittingBid}
                    className="flex-1 h-11 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition disabled:opacity-50"
                    data-testid="submit-bid-btn"
                  >
                    {submittingBid ? t('browseJobs.submitting') : t('browseJobs.submitBid')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      <CategoryManager
        isOpen={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        onCategoryChange={fetchCategories}
        isAdmin={isAdmin}
      />

      <Footer />
    </div>
  );
};

export default BrowseJobs;
